import * as B from '@blueprintjs/core';
import {useVirtualizer} from '@tanstack/react-virtual';
import 'chartjs-adapter-moment';
import classnames from 'classnames';
import * as I from 'immutable';
import React from 'react';
import {type Column, Row, useFilters, useRowSelect, useTable} from 'react-table';

import cs from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysisTable.module.scss';
import {ProjectFilterMenu} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/ProjectFilterMenu';
import {TagFilterMenu} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/TagFilterMenu';
import {
  ChartDatasetPoints,
  FeatureStatProperties,
  MultifeatureAnalysisConfig,
  TrendLineAggregation,
} from 'app/components/Dashboard/types';
import {
  MAX_SELECTABLE_FEATURES,
  ProjectFilter,
  TagFilter,
  formatAggregatedDate,
} from 'app/components/Dashboard/utils/chartUtils';
import {formatTableValue} from 'app/components/Dashboard/utils/tableUtils';
import {ApiOrganization, TagSetting} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {alphanumericSort} from 'app/utils/featureUtils';
import {makeProjectDashboardUrl} from 'app/utils/routeUtils';

// Extends the individual feature stats objects found on FeatureStatsApiResponse
// into a format react-table can handle
export type FeatureStatsRowData = {
  id: string;
  name: string;
  color?: string;
} & Partial<ChartDatasetPoints> &
  Partial<FeatureStatProperties>;

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
} | null;

// Refers to what element we want the hover interatction to fire on. For categorical charts,
// we want to fire on the color swatch near the name, for line charts we want to fire on the row.
type RowInteractionMode = 'color' | 'row' | null;

/**
 * Component to render a table of feature rows, as well the setters to affect a chart that
 * takes each row as a dataset.
 */
const MultiFeatureAnalysisTable: React.FunctionComponent<{
  filterStates: {
    tagIdsDict: Record<string, TagSetting>;
    projectIdsDict: Record<string, ApiProject>;
    tagFilter: TagFilter;
    projectFilter: ProjectFilter;
  };
  filterActions: {
    applyTagFilter: (tagIDs: Array<string>, tagFilterMode?: 'any' | 'all' | 'none') => void;
    applyProjectFilter: (projectIDs: Array<string>, projectFilterMode?: 'any' | 'none') => void;
  };
  displayFeatureIds: Set<string>;
  setDisplayFeatureIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  rows: FeatureStatsRowData[];
  allDates: string[];
  showAggregateTrend: boolean;
  widgetConfig: MultifeatureAnalysisConfig;
  setHoveredFeatureId: React.Dispatch<React.SetStateAction<string | null>>;
  trendLine: ChartDatasetPoints;
  allowAverage?: boolean;
  additionalInputs?: React.ReactNode;
  rowInteractionMode?: RowInteractionMode;
  nameColumnOverride?: string;
  trendLineAggregation?: TrendLineAggregation;
  organization?: I.ImmutableOf<ApiOrganization>;
  isExpanded?: boolean;
}> = ({
  filterStates,
  filterActions,
  displayFeatureIds,
  setDisplayFeatureIds,
  rows,
  allDates,
  showAggregateTrend,
  widgetConfig,
  setHoveredFeatureId,
  trendLine,
  allowAverage = true,
  additionalInputs,
  rowInteractionMode = 'row',
  nameColumnOverride = 'Property Name',
  trendLineAggregation = 'mean',
  organization,
  isExpanded = true,
}) => {
  const isCompact = widgetConfig.displayOptions?.chartHidden && !isExpanded;
  const [searchQuery, setSearchQuery] = React.useState('');
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const [sortConfig, setSortConfig] = React.useState<SortConfig>(null);
  const {tagIdsDict, tagFilter, projectIdsDict, projectFilter} = filterStates;
  const {tagIds, tagFilterMode} = tagFilter;
  const {applyTagFilter, applyProjectFilter} = filterActions;
  const {projectIds, projectFilterMode} = projectFilter;
  const {timeAggregation, layerKey} = widgetConfig;
  const featureStatsRows = React.useMemo<FeatureStatsRowData[]>(
    () =>
      rows ? rows.filter((row) => row.name.toLowerCase().includes(searchQuery.toLowerCase())) : [],
    [rows, searchQuery]
  );

  const allRows = React.useMemo<FeatureStatsRowData[]>(() => {
    const rows = [...featureStatsRows];
    if (sortConfig) {
      const {key, direction} = sortConfig;
      rows.sort((a, b) => alphanumericSort(a[key], b[key], direction));
    }
    // Always keep trendline row at top regardless of sort
    if (trendLine && allowAverage) {
      rows.unshift({
        id: 'trendline',
        name: trendLineAggregation === 'sum' ? 'Total' : 'Average',
        ...trendLine,
      } as FeatureStatsRowData);
    }
    return rows;
  }, [featureStatsRows, sortConfig, trendLine, allowAverage, trendLineAggregation]);

  const handleHeaderClick = React.useCallback((key: string) => {
    setSortConfig((currentSort) => {
      if (currentSort?.key === key) {
        return currentSort.direction === 'asc' ? {key, direction: 'desc'} : null;
      }
      return {key, direction: 'asc'};
    });
  }, []);

  const makePropertyLink = React.useCallback(
    (row: FeatureStatsRowData) => {
      if (row.projectId && row.id && organization) {
        return makeProjectDashboardUrl(organization, row.projectId, 'map', [row.id]);
      }
      return row.name;
    },
    [organization]
  );

  const allRowIds = React.useMemo(() => new Set(allRows.map((row) => row.id)), [allRows]);

  const columns = React.useMemo<Column<FeatureStatsRowData>[]>(
    () => [
      {
        id: 'selection',
        Header: () => (
          <div className={cs.checkboxCell}>
            {allRowIds.size < MAX_SELECTABLE_FEATURES ? (
              <B.Checkbox
                checked={displayFeatureIds.size === allRowIds.size}
                indeterminate={
                  displayFeatureIds.size > 0 && displayFeatureIds.size < allRowIds.size
                }
                onChange={() => {
                  if (displayFeatureIds.size === allRowIds.size) {
                    setDisplayFeatureIds(() => new Set());
                  } else {
                    setDisplayFeatureIds(() => allRowIds);
                  }
                }}
              />
            ) : null}
          </div>
        ),
        Cell: ({row}) => (
          <div className={cs.checkboxCell}>
            <B.Checkbox
              checked={
                displayFeatureIds.has(row.original.id) ||
                (row.original.id === 'trendline' && showAggregateTrend)
              }
            />
          </div>
        ),
        width: 40,
      },
      {
        id: 'name',
        accessor: 'name',
        width: 180,
        Header: ({column}) => (
          <div onClick={() => handleHeaderClick(column.id)}>
            {nameColumnOverride}
            <span>
              {sortConfig?.key === column.id ? (
                sortConfig.direction === 'desc' ? (
                  <B.Icon icon="caret-down" className={cs.icon} />
                ) : (
                  <B.Icon icon="caret-up" className={cs.icon} />
                )
              ) : (
                <B.Icon icon="double-caret-vertical" className={cs.icon} />
              )}
            </span>
          </div>
        ),
        Cell: ({row}) => (
          <div
            className={classnames({
              [cs.nameCellWithColor]: rowInteractionMode === 'color',
            })}
          >
            {rowInteractionMode === 'color' && (
              <ColorCell
                color={row.original.color}
                id={row.original.id}
                setHoveredFeatureId={setHoveredFeatureId}
              />
            )}
            <div title={row.original.name}>
              {/* If we have a projectId and a featureId, we can link to the property */}
              {row.original.projectId ? (
                <a
                  href={makePropertyLink(row.original)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.original.name}
                </a>
              ) : (
                row.original.name
              )}
            </div>
          </div>
        ),
      },
      ...allDates.map((date) => ({
        id: date,
        accessor: date,
        width: 100,
        Header: ({column}) => (
          <div onClick={() => handleHeaderClick(column.id)}>
            {formatAggregatedDate(date, timeAggregation, layerKey)}
            <span>
              {sortConfig?.key === column.id ? (
                sortConfig?.direction === 'desc' ? (
                  <B.Icon icon="caret-down" className={cs.icon} />
                ) : (
                  <B.Icon icon="caret-up" className={cs.icon} />
                )
              ) : (
                <B.Icon icon="double-caret-vertical" className={cs.icon} />
              )}
            </span>
          </div>
        ),
        Cell: ({row}) =>
          typeof row.original[date] === 'number' ? formatTableValue(row.original[date]) : '-',
      })),
    ],
    [
      allDates,
      displayFeatureIds,
      allRowIds,
      setDisplayFeatureIds,
      showAggregateTrend,
      nameColumnOverride,
      sortConfig,
      handleHeaderClick,
      rowInteractionMode,
      setHoveredFeatureId,
      timeAggregation,
      makePropertyLink,
    ]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows: tableRows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: allRows,
      getRowId: (row) => row.id,
    },
    useFilters,
    useRowSelect
  );

  // Break apart the trendline react-table row from the rows to be virualized
  const [trendlineTableRow, virtualTableRows] = React.useMemo(() => {
    if (!trendLine || !allowAverage) return [null, tableRows];
    const [trendlineRow, ...restRows] = tableRows;
    prepareRow(trendlineRow);
    return [trendlineRow, restRows];
  }, [trendLine, allowAverage, tableRows, prepareRow]);

  const rowVirtualizer = useVirtualizer({
    count: virtualTableRows.length,
    getScrollElement: () => tableContainerRef.current,
    getItemKey: React.useMemo(() => (index) => virtualTableRows[index].id, [virtualTableRows]),
    estimateSize: React.useCallback(() => 35, []), // Approximate row height
    overscan: 3,
  });

  const onRowClick = React.useCallback(
    (row: Row<FeatureStatsRowData>) => {
      setDisplayFeatureIds((prevFeatureIds) => {
        const newSelectedIds = new Set(prevFeatureIds);
        if (prevFeatureIds.has(row.id)) {
          newSelectedIds.delete(row.id);
        } else {
          newSelectedIds.add(row.id);
        }
        return newSelectedIds;
      });
    },
    [setDisplayFeatureIds]
  );

  // Memoize the row renderer
  const renderRow = React.useCallback(
    (virtualRow: any) => {
      const row = virtualTableRows[virtualRow.index];
      return (
        <TableRow
          key={row.id}
          row={row}
          isSelected={displayFeatureIds.has(row.id)}
          rowInteractionMode={rowInteractionMode}
          onRowClick={onRowClick}
          setHoveredFeatureId={setHoveredFeatureId}
          prepareRow={prepareRow}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: `${virtualRow.size}px`,
            transform: `translateY(${virtualRow.start}px)`,
          }}
        />
      );
    },
    [
      virtualTableRows,
      displayFeatureIds,
      rowInteractionMode,
      onRowClick,
      setHoveredFeatureId,
      prepareRow,
    ]
  );

  return (
    <div className={cs.multiFeatureAnalysisContainer}>
      <div className={cs.controlsContainer}>
        <B.InputGroup
          leftIcon={<B.Icon icon="search" size={12} className={cs.icon} />}
          placeholder={`Filter by ${nameColumnOverride.split(' ').join(' ').toLowerCase()}`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          rightElement={<B.Button minimal icon="cross" onClick={() => setSearchQuery('')} />}
          className={cs.searchInput}
        />
        {Object.keys(tagIdsDict).length > 0 && (
          <B.Popover
            content={
              <TagFilterMenu
                tagFilterMode={tagFilterMode}
                tagIds={tagIds}
                tagIdsDict={tagIdsDict}
                applyTagFilter={applyTagFilter}
              />
            }
            position="bottom-left"
          >
            <B.Button
              text={
                tagIds.length === 0
                  ? `Filter tags`
                  : tagIds.length === 1
                    ? `"${tagIdsDict[tagIds[0]]?.text}" tag`
                    : `${tagIds.length} tags`
              }
              icon={
                tagIds.length === 0 ? (
                  <B.Icon icon="filter" size={14} />
                ) : tagIds.length > 1 ? (
                  <B.Icon icon="tags" size={14} />
                ) : (
                  <B.Icon icon="tag" size={14} />
                )
              }
              minimal
              active={tagIds.length > 0}
            />
          </B.Popover>
        )}
        {Object.keys(projectIdsDict).length > 0 && (
          <B.Popover
            content={
              <ProjectFilterMenu
                projectFilterMode={projectFilterMode}
                projectIds={projectIds}
                projectIdsDict={projectIdsDict}
                applyProjectFilter={applyProjectFilter}
              />
            }
          >
            <B.Button
              minimal
              icon={
                projectIds.length === 0 ? (
                  <B.Icon icon="filter" size={14} />
                ) : (
                  <B.Icon icon="folder-open" size={14} />
                )
              }
              text={
                projectIds.length === 0
                  ? `Filter portfolios`
                  : projectIds.length === 1
                    ? `${projectIdsDict[projectIds[0]]?.name}`
                    : `${projectIds.length} portfolios`
              }
              active={projectIds.length > 0}
            />
          </B.Popover>
        )}
        {additionalInputs}
      </div>

      <div
        ref={tableContainerRef}
        className={classnames(cs.tableContainer, isCompact && cs.compact)}
      >
        <table {...getTableProps()} className={cs.table}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const {key, ...headerGroupProps} = headerGroup.getHeaderGroupProps();
              return (
                <tr key={key} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const {key, ...headerProps} = column.getHeaderProps();
                    return (
                      <th key={key} {...headerProps} style={{width: column.width}}>
                        {column.render('Header')}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
            {trendlineTableRow && (
              <tr
                {...trendlineTableRow.getRowProps()}
                className={showAggregateTrend ? cs.selected : ''}
                onClick={() => onRowClick(trendlineTableRow)}
                onMouseEnter={() => setHoveredFeatureId(trendlineTableRow.original.id)}
                onMouseLeave={() => setHoveredFeatureId(null)}
              >
                {trendlineTableRow.cells.map((cell) => {
                  const {key, ...cellProps} = cell.getCellProps();
                  return (
                    <th
                      key={key}
                      {...cellProps}
                      style={{width: cell.column.width, fontWeight: 'normal'}}
                    >
                      {cell.render('Cell')}
                    </th>
                  );
                })}
              </tr>
            )}
          </thead>

          {/* Virtualized rows */}
          <tbody
            {...getTableBodyProps()}
            style={{
              position: 'relative',
              height: `${rowVirtualizer.getTotalSize()}px`,
            }}
          >
            {rowVirtualizer.getVirtualItems().map(renderRow)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Memoize the TableRow component
const TableRow = React.memo<{
  row: Row<FeatureStatsRowData>;
  isSelected: boolean;
  rowInteractionMode: RowInteractionMode;
  onRowClick: (row: Row<FeatureStatsRowData>) => void;
  setHoveredFeatureId: React.Dispatch<React.SetStateAction<string | null>>;
  prepareRow: (row: Row<FeatureStatsRowData>) => void;
  style?: React.CSSProperties;
  className?: string;
}>(
  ({
    row,
    isSelected,
    rowInteractionMode,
    onRowClick,
    setHoveredFeatureId,
    prepareRow,
    style,
    className,
  }) => {
    prepareRow(row);
    const {key, ...rowProps} = row.getRowProps();
    return (
      <tr
        key={key}
        {...rowProps}
        style={style}
        className={classnames(cs.tableRow, isSelected && cs.selected, className)}
        onClick={() => onRowClick(row)}
        onMouseEnter={() => rowInteractionMode === 'row' && setHoveredFeatureId(row.original.id)}
        onMouseLeave={() => rowInteractionMode === 'row' && setHoveredFeatureId(null)}
      >
        {row.cells.map((cell) => {
          const {key, ...cellProps} = cell.getCellProps();
          return (
            <td key={key} {...cellProps} style={{width: cell.column.width}}>
              {cell.render('Cell')}
            </td>
          );
        })}
      </tr>
    );
  }
);

// Separate component to handle hover state for color cells
// There is a bug (I think somewhere in the virtualization dep) where the
// onMouseLeave applied to the element is not firing. Here we are using
// ref architecture to manage reactivity instead.
const ColorCell = React.memo<{
  color?: string;
  id: string;
  setHoveredFeatureId: React.Dispatch<React.SetStateAction<string | null>>;
}>(({color, id, setHoveredFeatureId}) => {
  // Track hover state with a ref to avoid reset on re-renders
  const isHoveredRef = React.useRef(false);

  // Clean up on unmount
  React.useEffect(() => {
    return () => {
      if (isHoveredRef.current) {
        setHoveredFeatureId(null);
      }
    };
  }, [setHoveredFeatureId]);

  return (
    <div
      className={cs.colorCell}
      style={{backgroundColor: color}}
      onMouseEnter={() => {
        isHoveredRef.current = true;
        setHoveredFeatureId(id);
      }}
      onMouseLeave={() => {
        isHoveredRef.current = false;
        setHoveredFeatureId(null);
      }}
    />
  );
});

export default MultiFeatureAnalysisTable;
